import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';

/**
 * Parlay Record — the public record of the Parlay Builder's
 * suggested parlays (Winning Features addendum).
 *
 * Once a day the parlay-archive-daily function archives ONE
 * canonical set of suggestions (per sport with games that day, per
 * mode, at the builder's default 3 legs) to `parlayHistory`; the
 * grading runs settle them into `parlayGrades`. Those collections
 * are Admin-SDK-only in Firestore rules, so this page reads the
 * aggregate through the parlay-record function — no login, no
 * Premium gate: the record is the marketing.
 *
 * The rules block below prints the record's rules exactly as the
 * grader implements them (utils/parlayGrading): push legs reprice,
 * flat 1-unit staking, and the record begins the day the archive
 * shipped — earlier suggestions were never stored and are not
 * counted, backfilled or reconstructed.
 */

const SPORT_LABELS = {
  americanfootball_nfl: 'NFL',
  americanfootball_ncaaf: 'NCAAF',
  basketball_nba: 'NBA',
  icehockey_nhl: 'NHL'
};

const MODE_LABELS = {
  safest: 'Safest',
  value: 'Best Value'
};

// Same footer text as the Parlay Builder card, verbatim.
const DISCLAIMER =
  '21+. For information and entertainment only. Not a sportsbook. No pick is guaranteed. Bet only where legal. Gambling problem? Call 1-800-GAMBLER (NY: 877-8-HOPENY).';

const functionUrl = () =>
  window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions/parlay-record'
    : '/.netlify/functions/parlay-record';

const formatAmerican = (american) =>
  typeof american === 'number' ? (american > 0 ? `+${american}` : `${american}`) : '—';

const formatPct = (fraction) =>
  typeof fraction === 'number' ? `${(fraction * 100).toFixed(1)}%` : '—';

const formatUnits = (units) => {
  if (typeof units !== 'number') return '—';
  return `${units > 0 ? '+' : ''}${units.toFixed(2)}u`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(iso));
  } catch (e) {
    return '';
  }
};

const RESULT_CHIP = {
  win: { label: 'Won', color: 'success' },
  loss: { label: 'Lost', color: 'error' },
  push: { label: 'Push', color: 'warning' }
};

const LEG_RESULT_CHIP = {
  win: { label: 'won', color: 'success' },
  loss: { label: 'lost', color: 'error' },
  push: { label: 'push', color: 'warning' }
};

const StatBox = ({ label, value }) => (
  <Box sx={{ textAlign: 'center', flex: 1, minWidth: 110 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 800 }}>
      {value}
    </Typography>
  </Box>
);

const StatsRow = ({ title, stats }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 1.5,
      flexWrap: 'wrap',
      py: 0.75
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 130 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {stats.wins}W–{stats.losses}L{stats.pushes ? `–${stats.pushes}P` : ''}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {formatPct(stats.winPct)} win rate
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        color: stats.units > 0 ? 'success.main' : stats.units < 0 ? 'error.main' : 'inherit'
      }}
    >
      {formatUnits(stats.units)}
    </Typography>
  </Box>
);

const ParlayRecordScreen = () => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(functionUrl(), { signal: controller.signal })
      .then(async (response) => {
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error((payload && payload.error) || `Record failed (${response.status})`);
        }
        return payload;
      })
      .then((payload) => {
        setRecord(payload);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Could not load the parlay record right now.');
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const overall = record && record.overall ? record.overall : null;
  const settledSlips = record && Array.isArray(record.settledSlips) ? record.settledSlips : [];
  const byMode = record && record.byMode ? record.byMode : {};
  const bySport = record && record.bySport ? record.bySport : {};

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
        Parlay Record
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        How the Parlay Builder&apos;s suggested parlays have actually done — every archived slip,
        graded against final scores, nothing cherry-picked and nothing removed.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && record && (
        <>
          {/* Headline record */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <StatBox
                  label="RECORD"
                  value={
                    overall
                      ? `${overall.wins}–${overall.losses}${overall.pushes ? `–${overall.pushes}` : ''}`
                      : '—'
                  }
                />
                <StatBox label="WIN RATE" value={overall ? formatPct(overall.winPct) : '—'} />
                <StatBox label="UNITS" value={overall ? formatUnits(overall.units) : '—'} />
                <StatBox
                  label="PENDING SLIPS"
                  value={typeof record.pendingCount === 'number' ? record.pendingCount : '—'}
                />
                <StatBox label="RECORD SINCE" value={formatDate(record.recordStart) || '—'} />
              </Box>

              {(Object.keys(byMode).length > 0 || Object.keys(bySport).length > 0) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {Object.keys(byMode).length > 0 && (
                      <Box sx={{ flex: 1, minWidth: 280 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          By mode
                        </Typography>
                        {Object.entries(byMode).map(([mode, stats]) => (
                          <StatsRow key={mode} title={MODE_LABELS[mode] || mode} stats={stats} />
                        ))}
                      </Box>
                    )}
                    {Object.keys(bySport).length > 0 && (
                      <Box sx={{ flex: 1, minWidth: 280 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          By sport
                        </Typography>
                        {Object.entries(bySport).map(([sport, stats]) => (
                          <StatsRow
                            key={sport}
                            title={SPORT_LABELS[sport] || sport}
                            stats={stats}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* Settled slips */}
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            Settled slips
          </Typography>
          {settledSlips.length === 0 ? (
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  The record is just getting started — the first archived slips settle once their
                  games finish. Check back after the next slate.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {settledSlips.map((slip) => {
                const chip = RESULT_CHIP[slip.result] || RESULT_CHIP.push;
                return (
                  <Card key={slip.parlayId}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}
                      >
                        <Chip
                          label={MODE_LABELS[slip.mode] || slip.mode || ''}
                          size="small"
                          sx={{ bgcolor: '#F97316', color: '#FFFFFF', fontWeight: 700 }}
                        />
                        <Chip
                          label={SPORT_LABELS[slip.sport] || slip.sport || ''}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {slip.legsCount}-leg parlay · {formatAmerican(slip.combinedAmerican)}
                        </Typography>
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color:
                                slip.units > 0
                                  ? 'success.main'
                                  : slip.units < 0
                                    ? 'error.main'
                                    : 'inherit'
                            }}
                          >
                            {formatUnits(slip.units)}
                          </Typography>
                          <Chip label={chip.label} size="small" color={chip.color} />
                        </Box>
                      </Box>
                      <Stack spacing={0.5}>
                        {slip.legs.map((leg, i) => {
                          const legChip = leg.result ? LEG_RESULT_CHIP[leg.result] : null;
                          return (
                            <Box
                              key={i}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {leg.selection}
                              </Typography>
                              {leg.finalScore && (
                                <Typography variant="caption" color="text.secondary">
                                  Final: {leg.finalScore}
                                </Typography>
                              )}
                              {legChip && (
                                <Chip label={legChip.label} size="small" color={legChip.color} />
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                {formatAmerican(leg.american)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Stack>
                      {slip.settledAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Settled {formatDate(slip.settledAt)}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}

          {/* The rules, printed exactly as graded */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                How this record works
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  <strong>What counts:</strong> once a day, we archive the Parlay Builder&apos;s
                  suggested slip for each sport with games that day — 3 legs, in both Safest and
                  Best Value mode — exactly as built at archive time. If a sport has no eligible
                  games that day, no slip is recorded for it. Slips you build yourself in the
                  builder are not part of this record.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Push legs:</strong> if a leg pushes, it is removed and the slip is
                  repriced on the remaining legs — it wins if all remaining legs win, at the
                  reduced combined odds. If every leg pushes, the slip is a push.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Staking:</strong> flat 1 unit on every slip. A win pays (combined
                  decimal − 1) units, a loss is −1 unit, a push is 0. Win rate excludes pushes.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Record begins {formatDate(record.recordStart) || 'with the first archived slip'}.</strong>{' '}
                  Suggested parlays built before then were never stored, so they are not counted
                  here — the record starts when the archive did, not when the builder launched.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 3, textAlign: 'center' }}
      >
        {DISCLAIMER}
      </Typography>
    </Box>
  );
};

export default ParlayRecordScreen;
